import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  MenuItem,
  Alert,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import { EntityStatus } from "../types";
import { apiService } from "../api/services/apiService";
import { resolveReturnDestination } from "../utils/navigationReturn";

interface AddFaqProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const AddFaq: React.FC<AddFaqProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(resolveReturnDestination(returnPath, returnSearch, "faqs"));

  /* -------------------- Form state -------------------- */
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [_tenantIdState, setTenantId] = useState(tenantId || "");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.ACTIVE);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");

  /* -------------------- UI state -------------------- */
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /* -------------------- Handlers -------------------- */

  const handleAddFaq = async () => {
    setSubmitting(true);
    setErrorMessage(null);

    try {
      await apiService.createFaq({
        tenantId,
        name,
        description,
        status,
        question,
        answer,
      });

      setSuccessOpen(true);

      setTimeout(() => {
        goBack();
      }, 800);
    } catch (err: any) {
      console.error("Create FAQ failed:", err);
      setErrorMessage(err.message || "Failed to create FAQ");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add FAQ"
        pageDescription="Create a new FAQ with question and answer details."
      />

      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            maxWidth: "80%",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />

            <TextField
              select
              label="Tenant ID"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              fullWidth
            >
              <MenuItem value={tenantId || ''}>{tenantId}</MenuItem>
            </TextField>

            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as EntityStatus)
              }
              fullWidth
            >
              {Object.values(EntityStatus).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Answer"
              multiline
              rows={2}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              fullWidth
              required
            />

            {errorMessage && (
              <Alert severity="error">{errorMessage}</Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleAddFaq}
                disabled={submitting}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {submitting ? "Creating..." : "Add FAQ"}
              </Button>

              <Button
                variant="outlined"
                onClick={goBack}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={successOpen} autoHideDuration={2000}>
        <Alert severity="success">
          FAQ created successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddFaq;
